import React, {
  createContext,
  type PropsWithChildren,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react'

type ModalContent = {
  body: ReactNode
  label?: string
  className?: string
  closable?: boolean
  /**
   * Used for accessibility reasons.
   * Set to true on modal alert dialogs that interrupt a user's workflow
   * to communicate an important message and requires a response.
   */
  isAlert?: boolean
  onClose?: () => void
}

type ModalContentType = ModalContent | null

type ModalContextProps = {
  content: ModalContentType
  setContent: (content: ModalContentType) => void
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  openModal: (content: ModalContentType) => void
  closeModal: () => void
}

const ModalContext = createContext<ModalContextProps | null>(null)

const ModalProvider = ({ children }: PropsWithChildren<{}>) => {
  const [content, setContent] = useState<ModalContentType>(null)
  const [isOpen, setIsOpen] = useState(false)

  const value = useMemo<ModalContextProps>(
    () => ({
      content,
      setContent,
      isOpen,
      setIsOpen,
      openModal: (modalContent) => {
        setContent(modalContent)
        setIsOpen(true)
      },
      closeModal: () => {
        setIsOpen(false)
      },
    }),
    [content, isOpen]
  )

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
}

function useModal() {
  const context = useContext(ModalContext)

  if (context === null) {
    throw new Error('useModal must be used within a ModalProvider component')
  }

  return context
}

export { useModal, ModalProvider }
